import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../../utils/env';
import './DeploymentPage.css';

const API_BASE = getApiBaseUrl();

const SERVICES = [
  { id: 'frontend', label: 'Frontend', desc: 'React app + nginx' },
  { id: 'backend', label: 'Backend', desc: 'Flask API + Redis sidecar' },
  { id: 'graphbuilder', label: 'GraphBuilder', desc: 'FastAPI + Platform Engine' },
  { id: 'worker', label: 'Worker', desc: 'Kafka document processor' },
];

const BASE_IMAGES = [
  { id: 'backend', label: 'Backend Base', desc: 'Python 3.11 + pip deps' },
  { id: 'graphbuilder', label: 'GraphBuilder Base', desc: 'Python 3.12 + pip deps + spaCy' },
];

const SENSITIVE_KEYS = new Set([
  'DB_PASSWORD', 'SECRET_KEY', 'REDIS_PASSWORD',
  'OPENAI_API_KEY', 'PINECONE_API_KEY',
  'db_password', 'pinecone_api_key',
]);

const LOCAL_GROUPS = [
  { label: 'Database (YugabyteDB)', keys: ['DB_HOST', 'DB_PORT', 'DB_NAME', 'DB_USER', 'DB_PASSWORD', 'DB_SSLMODE'] },
  { label: 'Redis', keys: ['REDIS_HOST', 'REDIS_PORT', 'REDIS_DB', 'REDIS_PASSWORD', 'REDIS_SSL', 'SESSION_EXPIRY_HOURS'] },
  { label: 'OpenAI', keys: ['OPENAI_API_KEY', 'OPENAI_BASE', 'OPENAI_EMBED_MODEL', 'OPENAI_CHAT_MODEL'] },
  { label: 'Pinecone', keys: ['PINECONE_API_KEY', 'PINECONE_ENVIRONMENT', 'PINECONE_INDEX'] },
  { label: 'Kafka', keys: ['KAFKA_BOOTSTRAP_SERVERS', 'KAFKA_DOCUMENT_PROCESSING_TOPIC'] },
  { label: 'Services', keys: ['DOCUMENT_PROCESS_API_URL', 'FRONTEND_URL'] },
  { label: 'Flask', keys: ['FLASK_ENV', 'PORT', 'SECRET_KEY'] },
];

const TFVARS_GROUPS = [
  { label: 'Database (YugabyteDB)', keys: ['db_host', 'db_port', 'db_name', 'db_user', 'db_password', 'db_sslmode'] },
  { label: 'Pinecone', keys: ['pinecone_api_key', 'pinecone_environment', 'pinecone_index'] },
  { label: 'OpenAI', keys: ['openai_embed_model', 'openai_chat_model', 'openai_api_version', 'openai_subdomain_name', 'openai_gpt4o_version', 'openai_gpt4o_capacity', 'openai_gpt4o_mini_version', 'openai_gpt4o_mini_capacity', 'openai_embedding_version', 'openai_embedding_capacity', 'openai_max_rpm', 'gb_max_concurrent_chunks'] },
  { label: 'Model', keys: ['model_version'] },
  { label: 'Backend Compute', keys: ['backend_cpu', 'backend_memory', 'backend_min_replicas', 'backend_max_replicas'] },
  { label: 'GraphBuilder Compute', keys: ['graphbuilder_cpu', 'graphbuilder_memory', 'graphbuilder_min_replicas', 'graphbuilder_max_replicas'] },
  { label: 'Frontend Compute', keys: ['frontend_cpu', 'frontend_memory', 'frontend_min_replicas', 'frontend_max_replicas'] },
  { label: 'Worker Compute', keys: ['worker_cpu', 'worker_memory', 'worker_min_replicas', 'worker_max_replicas'] },
  { label: 'Kafka', keys: ['kafka_cpu', 'kafka_memory', 'kafka_default_partitions', 'kafka_group_id'] },
  { label: 'Application', keys: ['project_name', 'location', 'resource_group_name', 'image_tag', 'session_expiry_hours', 'messaging_mode', 'document_process_api_timeout', 'cors_custom_origins'] },
  { label: 'Monitoring', keys: ['enable_application_insights', 'log_retention_days'] },
];

const LOCAL_TO_TFVARS_MAP = {
  DB_HOST: 'db_host',
  DB_PORT: 'db_port',
  DB_NAME: 'db_name',
  DB_USER: 'db_user',
  DB_PASSWORD: 'db_password',
  DB_SSLMODE: 'db_sslmode',
  PINECONE_API_KEY: 'pinecone_api_key',
  PINECONE_ENVIRONMENT: 'pinecone_environment',
  PINECONE_INDEX: 'pinecone_index',
  OPENAI_EMBED_MODEL: 'openai_embed_model',
  OPENAI_CHAT_MODEL: 'openai_chat_model',
};

function authHeaders() {
  const h = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  const sid = localStorage.getItem('sessionId');
  if (sid) h['X-Session-ID'] = sid;
  return h;
}

function CredentialsSection() {
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('local');
  const [configData, setConfigData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [edits, setEdits] = useState({});
  const [revealed, setRevealed] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const loadConfig = useCallback((tab) => {
    setLoading(true);
    setError(null);
    setSaveMsg(null);
    setEdits({});
    setRevealed(new Set());

    const url = tab === 'local'
      ? `${API_BASE}/deploy/config/local`
      : `${API_BASE}/deploy/config/tfvars/${tab}`;

    fetch(url, { credentials: 'include', headers: authHeaders() })
      .then((r) => {
        if (!r.ok) return r.json().then((d) => Promise.reject(d));
        return r.json();
      })
      .then((data) => {
        setConfigData((prev) => ({ ...prev, [tab]: data }));
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.error || err?.message || 'Failed to load configuration');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (expanded && !configData[activeTab]) {
      loadConfig(activeTab);
    }
  }, [expanded, activeTab, configData, loadConfig]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setEdits({});
    setRevealed(new Set());
    setSaveMsg(null);
    setError(null);
    if (!configData[tab]) {
      loadConfig(tab);
    }
  };

  const toggleReveal = (key) => {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleEdit = (key, value) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
    setSaveMsg(null);
  };

  const getDisplayValue = (entry) => {
    if (edits[entry.key] !== undefined) return edits[entry.key];
    if (SENSITIVE_KEYS.has(entry.key) && !revealed.has(entry.key)) return entry.maskedValue;
    return entry.value;
  };

  const handleSave = useCallback(() => {
    if (Object.keys(edits).length === 0) return;
    setSaving(true);
    setSaveMsg(null);

    const url = activeTab === 'local'
      ? `${API_BASE}/deploy/config/local`
      : `${API_BASE}/deploy/config/tfvars/${activeTab}`;

    fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify({ values: edits }),
    })
      .then((r) => {
        if (!r.ok) return r.json().then((d) => Promise.reject(d));
        return r.json();
      })
      .then(() => {
        setSaveMsg('Saved successfully');
        setSaving(false);
        setEdits({});
        setConfigData((prev) => ({ ...prev, [activeTab]: null }));
        loadConfig(activeTab);
      })
      .catch((err) => {
        setSaveMsg(`Error: ${err?.error || err?.message || 'Save failed'}`);
        setSaving(false);
      });
  }, [activeTab, edits, loadConfig]);

  const handleCreateFromExample = useCallback(() => {
    setSaving(true);
    setSaveMsg(null);

    const allValues = {};
    const entries = configData[activeTab]?.entries || [];
    for (const e of entries) {
      if (e.type === 'kv') allValues[e.key] = e.value;
    }

    fetch(`${API_BASE}/deploy/config/tfvars/${activeTab}`, {
      method: 'PUT',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify({ values: allValues }),
    })
      .then((r) => {
        if (!r.ok) return r.json().then((d) => Promise.reject(d));
        return r.json();
      })
      .then(() => {
        setSaveMsg('terraform.tfvars created successfully');
        setSaving(false);
        setEdits({});
        setConfigData((prev) => ({ ...prev, [activeTab]: null }));
        loadConfig(activeTab);
      })
      .catch((err) => {
        setSaveMsg(`Error: ${err?.error || err?.message || 'Create failed'}`);
        setSaving(false);
      });
  }, [activeTab, configData, loadConfig]);

  const handleCopyFromLocal = useCallback(() => {
    const localData = configData['local'];

    const doCopy = (localEntries) => {
      const localKv = {};
      for (const e of localEntries) {
        if (e.type === 'kv') localKv[e.key] = e.value;
      }
      const newEdits = {};
      for (const [envKey, tfKey] of Object.entries(LOCAL_TO_TFVARS_MAP)) {
        const val = localKv[envKey];
        if (val !== undefined && val !== '') {
          newEdits[tfKey] = val;
        }
      }
      if (Object.keys(newEdits).length === 0) {
        setSaveMsg('No matching values found in local config');
        return;
      }
      setEdits((prev) => ({ ...prev, ...newEdits }));
      setSaveMsg(`Copied ${Object.keys(newEdits).length} values from local`);
    };

    if (localData?.entries) {
      doCopy(localData.entries);
    } else {
      fetch(`${API_BASE}/deploy/config/local`, { credentials: 'include', headers: authHeaders() })
        .then((r) => r.json())
        .then((d) => {
          setConfigData((prev) => ({ ...prev, local: d }));
          doCopy(d.entries || []);
        })
        .catch(() => setSaveMsg('Error: Could not load local config'));
    }
  }, [configData]);

  const data = configData[activeTab];
  const groups = activeTab === 'local' ? LOCAL_GROUPS : TFVARS_GROUPS;
  const kvEntries = data?.entries?.filter((e) => e.type === 'kv') || [];
  const groupedKeys = new Set(groups.flatMap((g) => g.keys));
  const ungrouped = kvEntries.filter((e) => !groupedKeys.has(e.key));
  const hasEdits = Object.keys(edits).length > 0;

  const TABS = [
    { id: 'local', label: 'Local (.env)' },
    { id: 'test', label: 'Test' },
    { id: 'fullprod', label: 'Production' },
  ];

  const renderField = (entry) => {
    const isSensitive = SENSITIVE_KEYS.has(entry.key);
    const displayVal = getDisplayValue(entry);
    const isEdited = edits[entry.key] !== undefined;

    return (
      <div key={entry.key} className={`cred-field ${isEdited ? 'cred-field--edited' : ''}`}>
        <label className="cred-field__label">{entry.key}</label>
        <div className="cred-field__input-wrap">
          <input
            type={isSensitive && !revealed.has(entry.key) ? 'password' : 'text'}
            className="deploy-input cred-field__input"
            value={displayVal}
            onChange={(e) => handleEdit(entry.key, e.target.value)}
            onFocus={() => { if (isSensitive && !revealed.has(entry.key)) toggleReveal(entry.key); }}
          />
          {isSensitive && (
            <button
              type="button"
              className="cred-field__reveal"
              onClick={() => toggleReveal(entry.key)}
              title={revealed.has(entry.key) ? 'Hide' : 'Reveal'}
            >
              {revealed.has(entry.key) ? 'Hide' : 'Show'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="user-home-layout__card deploy-section cred-section">
      <button
        className="cred-section__toggle"
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        <h2 className="user-home-layout__section-title" style={{ margin: 0 }}>
          Credentials &amp; Configuration
        </h2>
        <span className={`cred-section__chevron ${expanded ? 'cred-section__chevron--open' : ''}`}>
          &#9662;
        </span>
      </button>

      {expanded && (
        <div className="cred-section__body">
          <div className="cred-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`cred-tab ${activeTab === t.id ? 'cred-tab--active' : ''}`}
                onClick={() => handleTabChange(t.id)}
                type="button"
              >
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'fullprod' && (
            <div className="cred-warning">
              Production environment -- changes take effect on next <code>terraform apply</code> or deploy.
            </div>
          )}

          {data?.fromExample && (
            <div className="cred-info">
              Loaded from <code>.tfvars.example</code> template. Saving will create <code>terraform.tfvars</code> with your values.
            </div>
          )}

          {loading && <p className="deploy-hint" style={{ padding: 16 }}>Loading configuration...</p>}
          {error && <p className="cred-error">{error}</p>}
          {!loading && !error && !data && (
            <p className="deploy-hint" style={{ padding: 16 }}>Click a tab to load configuration.</p>
          )}

          {!loading && !error && data && (
            <div className="cred-groups">
              {groups.map((group) => {
                const entries = group.keys
                  .map((k) => kvEntries.find((e) => e.key === k))
                  .filter(Boolean);
                if (entries.length === 0) return null;
                return (
                  <div key={group.label} className="cred-group">
                    <h3 className="cred-group__title">{group.label}</h3>
                    <div className="cred-group__fields">
                      {entries.map(renderField)}
                    </div>
                  </div>
                );
              })}
              {ungrouped.length > 0 && (
                <div className="cred-group">
                  <h3 className="cred-group__title">Other</h3>
                  <div className="cred-group__fields">
                    {ungrouped.map(renderField)}
                  </div>
                </div>
              )}

              <div className="cred-actions">
                {data?.fromExample && (
                  <button
                    className="deploy-btn deploy-btn--primary"
                    onClick={handleCreateFromExample}
                    disabled={saving}
                  >
                    {saving ? 'Creating...' : 'Create terraform.tfvars'}
                  </button>
                )}
                <button
                  className="deploy-btn deploy-btn--primary"
                  onClick={handleSave}
                  disabled={!hasEdits || saving}
                  title={!hasEdits ? 'Edit a field value to enable saving' : ''}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                {activeTab !== 'local' && (
                  <button
                    className="deploy-btn deploy-btn--copy"
                    onClick={handleCopyFromLocal}
                    disabled={saving}
                    type="button"
                  >
                    Copy from Local
                  </button>
                )}
                {hasEdits && (
                  <button
                    className="deploy-btn deploy-btn--secondary"
                    onClick={() => { setEdits({}); setSaveMsg(null); }}
                  >
                    Discard
                  </button>
                )}
                <button
                  className="deploy-btn deploy-btn--secondary"
                  onClick={() => { setConfigData((prev) => ({ ...prev, [activeTab]: null })); loadConfig(activeTab); }}
                  disabled={loading}
                >
                  Refresh
                </button>
                {saveMsg && (
                  <span className={`cred-save-msg ${saveMsg.startsWith('Error') ? 'cred-save-msg--error' : 'cred-save-msg--ok'}`}>
                    {saveMsg}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProvisionSection() {
  const [environment, setEnvironment] = useState('test');
  const [action, setAction] = useState('apply');
  const [imageTag, setImageTag] = useState('latest');
  const [skipBuild, setSkipBuild] = useState(false);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const logRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const handleProvision = useCallback(() => {
    const needsConfirm = environment === 'fullprod' || action === 'destroy';
    if (needsConfirm && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    setShowConfirm(false);
    setLogs([]);
    setRunning(true);
    setStatus('running');
    setElapsed(0);
    startTimer();

    const body = { environment, action, imageTag, skipBuild };

    fetch(`${API_BASE}/deploy/provision`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify(body),
    }).then((response) => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      function read() {
        reader.read().then(({ done, value }) => {
          if (done) {
            setRunning(false);
            stopTimer();
            setStatus((prev) => prev === 'running' ? 'done' : prev);
            return;
          }
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (const part of parts) {
            const lines = part.split('\n');
            let eventType = '';
            let eventData = '';
            for (const line of lines) {
              if (line.startsWith('event: ')) eventType = line.slice(7);
              else if (line.startsWith('data: ')) eventData = line.slice(6);
            }
            if (!eventType || !eventData) continue;
            try {
              const payload = JSON.parse(eventData);
              if (eventType === 'log') {
                setLogs((prev) => [...prev, { text: payload.text, cls: classifyLine(payload.text) }]);
              } else if (eventType === 'status') {
                setStatus('running');
              } else if (eventType === 'done') {
                const code = payload.exitCode;
                setStatus(code === 0 ? 'success' : 'failed');
                setRunning(false);
                stopTimer();
                setElapsed(payload.elapsed || Math.round((Date.now() - startTimeRef.current) / 1000));
                setLogs((prev) => [
                  ...prev,
                  {
                    text: code === 0
                      ? `Infrastructure provisioning completed in ${payload.elapsed}s`
                      : `Infrastructure provisioning failed (exit code ${code})`,
                    cls: code === 0 ? 'ok' : 'error',
                  },
                ]);
              } else if (eventType === 'error') {
                setLogs((prev) => [...prev, { text: payload.text, cls: 'error' }]);
                setStatus('failed');
                setRunning(false);
                stopTimer();
              }
            } catch { /* ignore */ }
          }
          read();
        }).catch(() => {
          setRunning(false);
          setStatus('failed');
          stopTimer();
        });
      }
      read();
    }).catch((err) => {
      setLogs((prev) => [...prev, { text: `Connection error: ${err.message}`, cls: 'error' }]);
      setRunning(false);
      setStatus('failed');
      stopTimer();
    });
  }, [environment, action, imageTag, skipBuild, showConfirm, startTimer, stopTimer]);

  const handleStop = useCallback(() => {
    fetch(`${API_BASE}/deploy/stop`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Accept': 'application/json' },
    }).catch(() => {});
  }, []);

  return (
    <div className="user-home-layout__card deploy-section provision-section">
      <button
        className="cred-section__toggle"
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        <h2 className="user-home-layout__section-title" style={{ margin: 0 }}>
          Provision Infrastructure (Terraform via GitHub Actions)
        </h2>
        <span className={`cred-section__chevron ${expanded ? 'cred-section__chevron--open' : ''}`}>
          &#9662;
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '16px 0 0' }}>
          <p className="deploy-hint" style={{ marginBottom: 16 }}>
            Creates or destroys the full Azure environment (resource group, container apps, storage, networking)
            by triggering a GitHub Actions workflow. Required before first deploy to a new environment.
          </p>

          <div className="deploy-row">
            <div className="deploy-field deploy-field--half">
              <label className="deploy-label">Environment</label>
              <div className="deploy-radio-group">
                <label className={`deploy-radio ${environment === 'test' ? 'active' : ''}`}>
                  <input type="radio" name="prov-env" value="test" checked={environment === 'test'}
                    onChange={() => { setEnvironment('test'); setShowConfirm(false); }} disabled={running} />
                  <span className="deploy-radio__label">Test</span>
                </label>
                <label className={`deploy-radio ${environment === 'fullprod' ? 'active' : ''}`}>
                  <input type="radio" name="prov-env" value="fullprod" checked={environment === 'fullprod'}
                    onChange={() => { setEnvironment('fullprod'); setShowConfirm(false); }} disabled={running} />
                  <span className="deploy-radio__label">Production</span>
                </label>
              </div>
            </div>
            <div className="deploy-field deploy-field--half">
              <label className="deploy-label">Action</label>
              <div className="deploy-radio-group">
                <label className={`deploy-radio ${action === 'apply' ? 'active' : ''}`}>
                  <input type="radio" name="prov-action" value="apply" checked={action === 'apply'}
                    onChange={() => { setAction('apply'); setShowConfirm(false); }} disabled={running} />
                  <span className="deploy-radio__label">Apply</span>
                  <span className="deploy-radio__desc">Create / update infrastructure</span>
                </label>
                <label className={`deploy-radio ${action === 'destroy' ? 'active' : ''}`}>
                  <input type="radio" name="prov-action" value="destroy" checked={action === 'destroy'}
                    onChange={() => { setAction('destroy'); setShowConfirm(false); }} disabled={running} />
                  <span className="deploy-radio__label">Destroy</span>
                  <span className="deploy-radio__desc">Tear down all resources</span>
                </label>
              </div>
            </div>
          </div>

          {action === 'apply' && (
            <div className="deploy-row">
              <div className="deploy-field deploy-field--half">
                <label className="deploy-label" htmlFor="prov-tag">Image Tag</label>
                <input id="prov-tag" type="text" className="deploy-input" value={imageTag}
                  onChange={(e) => setImageTag(e.target.value)} disabled={running} />
              </div>
              <div className="deploy-field deploy-field--half">
                <label className="deploy-toggle">
                  <input type="checkbox" checked={skipBuild}
                    onChange={(e) => setSkipBuild(e.target.checked)} disabled={running} />
                  <span className="deploy-toggle__switch" />
                  <span className="deploy-toggle__text">
                    <strong>Skip Image Build</strong>
                    <span className="deploy-hint">Use existing images in ACR</span>
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="deploy-actions">
            {!running && (
              <button
                className={`deploy-btn deploy-btn--primary ${(environment === 'fullprod' || action === 'destroy') ? 'deploy-btn--danger' : ''}`}
                onClick={handleProvision}
              >
                {showConfirm
                  ? `Confirm ${action === 'destroy' ? 'DESTROY' : ''} ${environment === 'fullprod' ? 'Production' : 'Test'}${action === 'destroy' ? '' : ' Provision'}`
                  : `${action === 'apply' ? 'Provision' : 'Destroy'} ${environment === 'fullprod' ? 'Production' : 'Test'}`}
              </button>
            )}
            {showConfirm && !running && (
              <button className="deploy-btn deploy-btn--secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
            )}
            {running && (
              <button className="deploy-btn deploy-btn--stop" onClick={handleStop}>Stop</button>
            )}
          </div>

          {(logs.length > 0 || running) && (
            <div className="deploy-terminal-wrapper" style={{ marginTop: 16 }}>
              <div className="deploy-terminal-header">
                <span className="deploy-terminal-title">Infrastructure Output</span>
                {logs.length > 0 && !running && (
                  <button className="deploy-terminal-clear" onClick={() => { setLogs([]); setStatus('idle'); }}>Clear</button>
                )}
              </div>
              <div className="deploy-terminal" ref={logRef}>
                {logs.map((entry, i) => (
                  <div key={i} className={`deploy-terminal__line ${entry.cls ? `deploy-terminal__line--${entry.cls}` : ''}`}>
                    {entry.text}
                  </div>
                ))}
              </div>
              <div className={`deploy-status-bar deploy-status-bar--${status}`}>
                <span className="deploy-status-bar__indicator" />
                <span className="deploy-status-bar__text">
                  {status === 'idle' && 'Ready'}
                  {status === 'running' && `Running... ${formatTime(elapsed)}`}
                  {status === 'success' && `Completed in ${formatTime(elapsed)}`}
                  {status === 'failed' && `Failed after ${formatTime(elapsed)}`}
                  {status === 'done' && `Done in ${formatTime(elapsed)}`}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function generateTag() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `local-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
}

function classifyLine(text) {
  if (/^==========/.test(text)) return 'step';
  if (/^\s+OK:/.test(text)) return 'ok';
  if (/^\s+WARN:/.test(text)) return 'warn';
  if (/^\s+ERROR:/.test(text)) return 'error';
  if (/^---\s/.test(text)) return 'build';
  return '';
}

function DeploymentPage() {
  const [available, setAvailable] = useState(null);
  const [environment, setEnvironment] = useState('test');
  const [selectedServices, setSelectedServices] = useState(['frontend', 'backend', 'graphbuilder']);
  const [tag, setTag] = useState(generateTag);
  const [baseTag, setBaseTag] = useState('v1');
  const [selectedBases, setSelectedBases] = useState([]);
  const [skipCommit, setSkipCommit] = useState(false);
  const [skipBuild, setSkipBuild] = useState(false);
  const [commitMessage, setCommitMessage] = useState('');
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);

  const logRef = useRef(null);
  const eventSourceRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/deploy/status`, { credentials: 'include', headers: { 'Accept': 'application/json' } })
      .then((r) => r.json())
      .then((d) => setAvailable(d.available))
      .catch(() => setAvailable(false));
  }, []);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  const toggleService = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const toggleBase = (id) => {
    setSelectedBases((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleDeploy = useCallback(() => {
    if (environment === 'fullprod' && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    setShowConfirm(false);

    if (selectedServices.length === 0) return;

    setLogs([]);
    setRunning(true);
    setStatus('running');
    setElapsed(0);
    startTimer();

    const body = {
      environment,
      services: selectedServices.join(','),
      tag: tag || '',
      baseTag,
      buildBase: selectedBases.join(','),
      skipCommit,
      skipBuild,
      commitMessage,
    };

    fetch(`${API_BASE}/deploy/run`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      function read() {
        reader.read().then(({ done, value }) => {
          if (done) {
            setRunning(false);
            stopTimer();
            if (status === 'running') setStatus('done');
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';

          for (const part of parts) {
            const lines = part.split('\n');
            let eventType = '';
            let eventData = '';

            for (const line of lines) {
              if (line.startsWith('event: ')) eventType = line.slice(7);
              else if (line.startsWith('data: ')) eventData = line.slice(6);
            }

            if (!eventType || !eventData) continue;

            try {
              const payload = JSON.parse(eventData);

              if (eventType === 'log') {
                setLogs((prev) => [...prev, { text: payload.text, cls: classifyLine(payload.text) }]);
              } else if (eventType === 'status') {
                setStatus('running');
              } else if (eventType === 'done') {
                const code = payload.exitCode;
                setStatus(code === 0 ? 'success' : 'failed');
                setRunning(false);
                stopTimer();
                setElapsed(payload.elapsed || Math.round((Date.now() - startTimeRef.current) / 1000));
                setLogs((prev) => [
                  ...prev,
                  {
                    text: code === 0
                      ? `Deployment completed successfully in ${payload.elapsed}s`
                      : `Deployment failed with exit code ${code}`,
                    cls: code === 0 ? 'ok' : 'error',
                  },
                ]);
              } else if (eventType === 'error') {
                setLogs((prev) => [...prev, { text: payload.text, cls: 'error' }]);
                setStatus('failed');
                setRunning(false);
                stopTimer();
              }
            } catch {
              // ignore parse errors
            }
          }

          read();
        }).catch(() => {
          setRunning(false);
          setStatus('failed');
          stopTimer();
        });
      }

      read();
    }).catch((err) => {
      setLogs((prev) => [...prev, { text: `Connection error: ${err.message}`, cls: 'error' }]);
      setRunning(false);
      setStatus('failed');
      stopTimer();
    });
  }, [environment, selectedServices, tag, baseTag, selectedBases, skipCommit, skipBuild, commitMessage, showConfirm, startTimer, stopTimer, status]);

  const handleStop = useCallback(() => {
    fetch(`${API_BASE}/deploy/stop`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Accept': 'application/json' },
    }).catch(() => {});
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  if (available === null) {
    return (
      <div className="user-home-layout__page deployment-page">
        <div className="user-home-layout__page-header">
          <h1 className="user-home-layout__page-title">Deployment</h1>
        </div>
        <div className="user-home-layout__card">
          <p className="user-home-layout__card-text">Checking deployment availability...</p>
        </div>
      </div>
    );
  }

  if (available === false) {
    return (
      <div className="user-home-layout__page deployment-page">
        <div className="user-home-layout__page-header">
          <h1 className="user-home-layout__page-title">Deployment</h1>
        </div>
        <div className="user-home-layout__card">
          <p className="user-home-layout__card-text">
            Deployment management is only available in local development mode.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-home-layout__page deployment-page">
      <div className="user-home-layout__page-header">
        <h1 className="user-home-layout__page-title">Deployment</h1>
        <p className="user-home-layout__page-subtitle">
          Build and deploy services to Azure Container Apps
        </p>
      </div>

      {/* Provision Infrastructure */}
      <ProvisionSection />

      {/* Environment & Services */}
      <div className="user-home-layout__card deploy-section">
        <h2 className="user-home-layout__section-title">Environment &amp; Target</h2>

        <div className="deploy-field">
          <label className="deploy-label">Environment</label>
          <div className="deploy-radio-group">
            <label className={`deploy-radio ${environment === 'test' ? 'active' : ''}`}>
              <input
                type="radio"
                name="environment"
                value="test"
                checked={environment === 'test'}
                onChange={() => setEnvironment('test')}
                disabled={running}
              />
              <span className="deploy-radio__label">Test</span>
              <span className="deploy-radio__desc">Safe testing environment (test.cerebralmachines.ai)</span>
            </label>
            <label className={`deploy-radio ${environment === 'fullprod' ? 'active' : ''}`}>
              <input
                type="radio"
                name="environment"
                value="fullprod"
                checked={environment === 'fullprod'}
                onChange={() => { setEnvironment('fullprod'); setShowConfirm(false); }}
                disabled={running}
              />
              <span className="deploy-radio__label">Production</span>
              <span className="deploy-radio__desc">Live environment (cerebralmachines.ai)</span>
            </label>
          </div>
        </div>

        <div className="deploy-field">
          <label className="deploy-label">Services</label>
          <div className="deploy-checkbox-group">
            {SERVICES.map((svc) => (
              <label key={svc.id} className={`deploy-checkbox ${selectedServices.includes(svc.id) ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedServices.includes(svc.id)}
                  onChange={() => toggleService(svc.id)}
                  disabled={running}
                />
                <span className="deploy-checkbox__label">{svc.label}</span>
                <span className="deploy-checkbox__desc">{svc.desc}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Image Settings */}
      <div className="user-home-layout__card deploy-section">
        <h2 className="user-home-layout__section-title">Image Settings</h2>

        <div className="deploy-row">
          <div className="deploy-field deploy-field--half">
            <label className="deploy-label" htmlFor="deploy-tag">Image Tag</label>
            <input
              id="deploy-tag"
              type="text"
              className="deploy-input"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="local-YYYYMMDD-HHmm"
              disabled={running}
            />
            <span className="deploy-hint">Leave empty to auto-generate</span>
          </div>
          <div className="deploy-field deploy-field--half">
            <label className="deploy-label" htmlFor="deploy-base-tag">Base Image Tag</label>
            <input
              id="deploy-base-tag"
              type="text"
              className="deploy-input"
              value={baseTag}
              onChange={(e) => setBaseTag(e.target.value)}
              disabled={running}
            />
            <span className="deploy-hint">Bump when versioning base images</span>
          </div>
        </div>

        <div className="deploy-field">
          <label className="deploy-label">Rebuild Base Images</label>
          <span className="deploy-hint" style={{ marginBottom: 8, display: 'block' }}>
            Only needed when requirements.txt changes. Adds ~8 min per base image.
          </span>
          <div className="deploy-checkbox-group">
            {BASE_IMAGES.map((b) => (
              <label key={b.id} className={`deploy-checkbox ${selectedBases.includes(b.id) ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedBases.includes(b.id)}
                  onChange={() => toggleBase(b.id)}
                  disabled={running}
                />
                <span className="deploy-checkbox__label">{b.label}</span>
                <span className="deploy-checkbox__desc">{b.desc}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="user-home-layout__card deploy-section">
        <h2 className="user-home-layout__section-title">Options</h2>

        <div className="deploy-row">
          <div className="deploy-field deploy-field--half">
            <label className="deploy-toggle">
              <input
                type="checkbox"
                checked={skipCommit}
                onChange={(e) => setSkipCommit(e.target.checked)}
                disabled={running}
              />
              <span className="deploy-toggle__switch" />
              <span className="deploy-toggle__text">
                <strong>Skip Git Commit</strong>
                <span className="deploy-hint">Use when you already pushed changes</span>
              </span>
            </label>
          </div>
          <div className="deploy-field deploy-field--half">
            <label className="deploy-toggle">
              <input
                type="checkbox"
                checked={skipBuild}
                onChange={(e) => setSkipBuild(e.target.checked)}
                disabled={running}
              />
              <span className="deploy-toggle__switch" />
              <span className="deploy-toggle__text">
                <strong>Skip Image Build</strong>
                <span className="deploy-hint">Only restart container revisions</span>
              </span>
            </label>
          </div>
        </div>

        <div className="deploy-field">
          <label className="deploy-label" htmlFor="deploy-commit-msg">Commit Message</label>
          <input
            id="deploy-commit-msg"
            type="text"
            className="deploy-input"
            value={commitMessage}
            onChange={(e) => setCommitMessage(e.target.value)}
            placeholder="Auto-generated from changed files"
            disabled={running || skipCommit}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="deploy-actions">
        {!running && (
          <button
            className={`deploy-btn deploy-btn--primary ${environment === 'fullprod' ? 'deploy-btn--danger' : ''}`}
            onClick={handleDeploy}
            disabled={selectedServices.length === 0}
          >
            {showConfirm
              ? 'Confirm Production Deploy'
              : `Deploy to ${environment === 'fullprod' ? 'Production' : 'Test'}`}
          </button>
        )}
        {showConfirm && !running && (
          <button className="deploy-btn deploy-btn--secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </button>
        )}
        {running && (
          <button className="deploy-btn deploy-btn--stop" onClick={handleStop}>
            Stop Deployment
          </button>
        )}
      </div>

      {/* Log Output */}
      <div className="deploy-terminal-wrapper">
        <div className="deploy-terminal-header">
          <span className="deploy-terminal-title">Deployment Output</span>
          {logs.length > 0 && (
            <button
              className="deploy-terminal-clear"
              onClick={() => { setLogs([]); setStatus('idle'); }}
              disabled={running}
            >
              Clear
            </button>
          )}
        </div>
        <div className="deploy-terminal" ref={logRef}>
          {logs.length === 0 && (
            <div className="deploy-terminal__empty">
              Deployment output will appear here...
            </div>
          )}
          {logs.map((entry, i) => (
            <div key={i} className={`deploy-terminal__line ${entry.cls ? `deploy-terminal__line--${entry.cls}` : ''}`}>
              {entry.text}
            </div>
          ))}
        </div>
        <div className={`deploy-status-bar deploy-status-bar--${status}`}>
          <span className="deploy-status-bar__indicator" />
          <span className="deploy-status-bar__text">
            {status === 'idle' && 'Ready'}
            {status === 'running' && `Running... ${formatTime(elapsed)}`}
            {status === 'success' && `Completed in ${formatTime(elapsed)}`}
            {status === 'failed' && `Failed after ${formatTime(elapsed)}`}
            {status === 'done' && `Done in ${formatTime(elapsed)}`}
          </span>
        </div>
      </div>

      {/* Credentials & Configuration */}
      <CredentialsSection />
    </div>
  );
}

export default DeploymentPage;
